import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Printer, CheckCircle, AlertTriangle, Thermometer, MapPin, Clock, User } from 'lucide-react';
import { format } from 'date-fns';
import EmailChecklistButton from '@/components/EmailChecklistButton';

interface ChecklistEntry {
  item_id: string;
  status: string;
  value?: string;
  note?: string;
  photo_url?: string;
  video_url?: string;
  voice_note_url?: string;
  corrective_action?: string;
  unit_label?: string;
}

interface Checklist {
  id: string;
  template_id: string;
  unit_id: string;
  status: string;
  scheduled_for: string;
  completed_at: string | null;
  signature_data?: string;
  user_name?: string;
  template: {
    name: string;
    items: any;
  };
  unit: {
    name: string;
    address?: string;
    timezone?: string;
    manager_name?: string;
    tenant: {
      hq_email?: string;
    };
  };
}

export default function ChecklistPrint() {
  const { checklistId } = useParams<{ checklistId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [checklist, setChecklist] = useState<Checklist | null>(null);
  const [entries, setEntries] = useState<Record<string, ChecklistEntry>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (checklistId) {
      fetchChecklist();
    }
  }, [checklistId]);

  const fetchChecklist = async () => {
    try {
      const { data, error } = await supabase
        .from('checklists')
        .select(`
          *,
          template:checklist_templates(name, items),
          unit:units(name, address, timezone, manager_name, tenant:tenants(hq_email))
        `)
        .eq('id', checklistId)
        .single();

      if (error) throw error;
      setChecklist(data);

      // Fetch entries
      const { data: existingEntries } = await supabase
        .from('checklist_entries')
        .select('*')
        .eq('checklist_id', checklistId);

      if (existingEntries) {
        const entriesMap: Record<string, ChecklistEntry> = {};
        existingEntries.forEach(entry => {
          entriesMap[entry.item_id] = {
            item_id: entry.item_id,
            status: entry.status || 'ok',
            value: entry.value || undefined,
            note: entry.note || undefined,
            photo_url: entry.photo_url || undefined,
            video_url: entry.video_url || undefined,
            voice_note_url: entry.voice_note_url || undefined,
            corrective_action: entry.corrective_action || undefined,
            unit_label: entry.unit_label || undefined,
          };
        });
        setEntries(entriesMap);
      }
    } catch (error) {
      console.error('Error fetching checklist:', error);
      navigate('/checklists');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ok':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'issue':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'critical':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      default:
        return <CheckCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ok':
        return 'bg-green-100 text-green-800';
      case 'issue':
        return 'bg-yellow-100 text-yellow-800';
      case 'critical':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading checklist...</p>
        </div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold mb-2">Checklist not found</h2>
        <Button onClick={() => navigate('/checklists')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Checklists
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Print controls - hidden in print */}
      <div className="print:hidden bg-background p-4 border-b">
        <div className="container mx-auto flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigate('/checklists')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Checklists
          </Button>
          <div className="flex items-center gap-3">
            <EmailChecklistButton 
              checklistId={checklistId!} 
              defaultRecipients={checklist?.unit.tenant.hq_email ? [checklist.unit.tenant.hq_email] : ['']}
            />
            <Button onClick={handlePrint} className="cta-primary">
              <Printer className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </div>
        </div>
      </div>

      {/* Print content */}
      <div className="container mx-auto p-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{checklist.template.name}</h1>
              <p className="text-lg text-gray-600">Checklist Completion Report</p>
            </div>
            <div className="text-right">
              <Badge className={`text-lg px-4 py-2 ${checklist.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {checklist.status.charAt(0).toUpperCase() + checklist.status.slice(1)}
              </Badge>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Location Details Card */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-gray-900">
                  <MapPin className="w-5 h-5 text-primary" />
                  Location Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Unit Name</p>
                  <p className="font-semibold text-gray-900">{checklist.unit.name}</p>
                </div>
                
                {checklist.unit.address && (
                  <div>
                    <p className="text-sm text-gray-600">Full Address</p>
                    <p className="font-medium text-gray-900 leading-relaxed">{checklist.unit.address}</p>
                  </div>
                )}
                
                <div className="flex gap-6">
                  {checklist.unit.timezone && (
                    <div>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Timezone
                      </p>
                      <p className="font-medium text-gray-900">{checklist.unit.timezone}</p>
                    </div>
                  )}
                  
                  {checklist.unit.manager_name && (
                    <div>
                      <p className="text-sm text-gray-600 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        Manager
                      </p>
                      <p className="font-medium text-gray-900">{checklist.unit.manager_name}</p>
                    </div>
                  )}
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Scheduled Date</p>
                  <p className="font-medium text-gray-900">{format(new Date(checklist.scheduled_for), 'MMM d, yyyy h:mm a')}</p>
                </div>
              </CardContent>
            </Card>

            {/* Completion Details Card */}
            <Card className="border border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg text-gray-900">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Completion Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {checklist.completed_at ? (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">Completion Date</p>
                      <p className="font-semibold text-gray-900">{format(new Date(checklist.completed_at), 'MMM d, yyyy h:mm a')}</p>
                    </div>
                    
                    {checklist.user_name && (
                      <div>
                        <p className="text-sm text-gray-600">Completed By</p>
                        <p className="font-semibold text-gray-900">{checklist.user_name}</p>
                      </div>
                    )}
                    
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <Badge className="bg-green-100 text-green-800 mt-1">
                        Completed Successfully
                      </Badge>
                    </div>
                  </>
                ) : (
                  <div>
                    <p className="text-sm text-gray-600">Status</p>
                    <Badge className="bg-yellow-100 text-yellow-800 mt-1">
                      In Progress
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Checklist Items */}
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-gray-900 border-b-2 border-gray-200 pb-2">
            Checklist Items
          </h2>

          {checklist.template.items?.map((item: any) => {
            const entry = entries[item.id];
            return (
              <Card key={item.id} className="border border-gray-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-3 text-lg">
                      {item.type === 'temperature' && <Thermometer className="w-5 h-5" />}
                      {item.label}
                      {item.required && <Badge variant="outline" className="text-xs">Required</Badge>}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(entry?.status || 'ok')}
                      <Badge className={getStatusColor(entry?.status || 'ok')}>
                        {entry?.status || 'OK'}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {entry?.value && (
                      <div>
                        <p className="font-semibold text-gray-700">Value:</p>
                        <p className="text-gray-900">{entry.value} {entry.unit_label || ''}</p>
                      </div>
                    )}
                    
                    {(item.min_value || item.max_value) && (
                      <div>
                        <p className="font-semibold text-gray-700">Expected Range:</p>
                        <p className="text-gray-900">{item.min_value || '—'} to {item.max_value || '—'}°C</p>
                      </div>
                    )}
                    
                    {entry?.note && (
                      <div className="md:col-span-2">
                        <p className="font-semibold text-gray-700">Notes:</p>
                        <p className="text-gray-900">{entry.note}</p>
                      </div>
                    )}
                    
                    {entry?.corrective_action && (
                      <div className="md:col-span-2">
                        <p className="font-semibold text-gray-700">Corrective Action:</p>
                        <p className="text-gray-900">{entry.corrective_action}</p>
                      </div>
                    )}

                    {(entry?.photo_url || entry?.video_url || entry?.voice_note_url) && (
                      <div className="md:col-span-2">
                        <p className="font-semibold text-gray-700">Media Evidence:</p>
                        <div className="flex gap-2 mt-1">
                          {entry.photo_url && <Badge variant="outline">Photo Captured</Badge>}
                          {entry.video_url && <Badge variant="outline">Video Recorded</Badge>}
                          {entry.voice_note_url && <Badge variant="outline">Voice Note</Badge>}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Signature section */}
        {checklist.signature_data && (
          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Digital Signature</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
              <div>
                <p className="text-gray-700 mb-2">Signed by: <strong>{checklist.user_name || 'Unknown'}</strong></p>
                {checklist.completed_at && (
                  <p className="text-gray-700">Date: {format(new Date(checklist.completed_at), 'MMM d, yyyy h:mm a')}</p>
                )}
                <Badge className="bg-blue-100 text-blue-800 mt-2">Digitally Signed</Badge>
              </div>
              <div className="signature-container">
                <div className="signature-box border-2 border-gray-300 bg-white rounded p-4 min-h-[120px] flex items-end justify-center">
                  <img 
                    src={checklist.signature_data} 
                    alt="Digital Signature"
                    className="max-h-[100px] max-w-full object-contain"
                    style={{ filter: 'contrast(1.2)' }}
                  />
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">Authorized Signature</p>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>Generated on {format(new Date(), 'MMM d, yyyy h:mm a')} | Crystal HACCP System</p>
        </div>
      </div>

      {/* Print styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @media print {
            body { -webkit-print-color-adjust: exact; }
            .print\\:hidden { display: none !important; }
            @page { margin: 0.75in; }
          }
        `
      }} />
    </div>
  );
}